import { useCallback, useRef, useState } from 'react'
import { agentplatService } from '../../services/agentplat'
import type { ChatMessage } from '../../services/agentplat'

interface UseChatOptions {
  agentId?: string | null
  brandContext?: string | null
  systemPrompt?: string | null
}

interface ChatSession {
  id: string
  title: string
  messages: Message[]
  createdAt: Date
}

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  createdAt: Date
}

export function useChat({ agentId = null, brandContext, systemPrompt }: UseChatOptions = {}) {
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Refs to always access latest state (avoids stale closures in callbacks)
  const sessionsRef = useRef<ChatSession[]>(sessions)
  sessionsRef.current = sessions
  const messagesRef = useRef<Message[]>(messages)
  messagesRef.current = messages

  const createNewSession = useCallback(() => {
    // Save current messages to current session (use ref to avoid stale closure)
    if (currentSessionId) {
      setSessions((prev) =>
        prev.map((s) => (s.id === currentSessionId ? { ...s, messages: messagesRef.current } : s))
      )
    }

    const newSession: ChatSession = {
      id: crypto.randomUUID(),
      title: 'New conversation',
      messages: [],
      createdAt: new Date(),
    }
    setSessions((prev) => [newSession, ...prev])
    setCurrentSessionId(newSession.id)
    setMessages([])
    return newSession
  }, [currentSessionId])

  const deleteSession = useCallback(
    (sessionId: string) => {
      setSessions((prev) => prev.filter((s) => s.id !== sessionId))
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null)
        setMessages([])
      }
    },
    [currentSessionId]
  )

  const selectSession = useCallback(
    (sessionId: string | null) => {
      if (sessionId === currentSessionId) return

      // Save current messages before switching (use ref to avoid stale closure)
      if (currentSessionId) {
        setSessions((prev) =>
          prev.map((s) => (s.id === currentSessionId ? { ...s, messages: messagesRef.current } : s))
        )
      }

      setCurrentSessionId(sessionId)

      if (sessionId) {
        // Use ref to get latest sessions state
        const session = sessionsRef.current.find((s) => s.id === sessionId)
        setMessages(session?.messages ?? [])
      } else {
        setMessages([])
      }
    },
    [currentSessionId]
  )

  const sendMessage = useCallback(
    async (content: string) => {
      let sessionId = currentSessionId

      if (!agentId) {
        const errorMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: 'Select an agent role before starting a chat.',
          createdAt: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
        return
      }

      // Create session if none exists
      if (!sessionId) {
        const newSession = createNewSession()
        sessionId = newSession.id
      }

      // Update session title with first message (use ref to avoid stale closure)
      if (messagesRef.current.length === 0) {
        setSessions((prev) =>
          prev.map((s) =>
            s.id === sessionId
              ? { ...s, title: content.slice(0, 40) + (content.length > 40 ? '...' : '') }
              : s
          )
        )
      }

      // Add user message
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        createdAt: new Date(),
      }
      setMessages((prev) => [...prev, userMessage])

      // Add placeholder assistant message
      const assistantMessageId = crypto.randomUUID()
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        createdAt: new Date(),
      }
      setMessages((prev) => [...prev, assistantMessage])

      setIsLoading(true)

      try {
        const historyMessages: ChatMessage[] = messagesRef.current
          .map((m) => ({ role: m.role, content: m.content }))
          .filter((m) => m.content && m.content.trim().length > 0)

        const conversation = historyMessages.length > 1
          ? historyMessages.map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n\n') + `\n\nUser: ${content}`
          : content

        const parts: string[] = []
        if (systemPrompt?.trim()) {
          parts.push(`[SYSTEM INSTRUCTIONS]\n${systemPrompt.trim()}`)
        }
        if (brandContext?.trim()) {
          parts.push(`[BRAND CONTEXT]\n${brandContext.trim()}`)
        }
        parts.push(conversation)
        const prompt = parts.join('\n\n')

        const result = await agentplatService.invokeAgent(agentId, prompt)

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId ? { ...m, content: result.content } : m
          )
        )
      } catch (error) {
        console.error('[useChat] Invoke error:', error)
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? { ...m, content: 'Sorry, there was an error. Please try again.' }
              : m
          )
        )
      } finally {
        setIsLoading(false)
      }
    },
    [currentSessionId, createNewSession, agentId, brandContext, systemPrompt]
  )

  const stopGeneration = useCallback(() => {
    // No-op: invoke-based requests cannot be cancelled mid-flight
  }, [])

  const clearSessions = useCallback(() => {
    setSessions([])
    setCurrentSessionId(null)
    setMessages([])
  }, [])

  return {
    sessions,
    currentSessionId,
    messages,
    isLoading,
    sendMessage,
    createNewSession,
    deleteSession,
    selectSession,
    stopGeneration,
    clearSessions,
  }
}
